import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import logo from '../assets/logo.png';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (pwd !== pwd2) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pwd });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50 flex flex-col" data-testid="reset-password-page">
      <div className="p-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-cmic-600 transition-colors">
          <ArrowLeft size={16} />Volver al inicio de sesión
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 -mt-12">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src={logo} alt="CMIC" className="h-16 w-auto mb-4" />
            <h1 className="text-2xl font-bold text-charcoal-800">Nueva contraseña</h1>
            <p className="text-sm text-charcoal-500 mt-1.5 text-center">Crea una contraseña segura para tu cuenta.</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            {success ? (
              <div className="text-center py-6" data-testid="reset-success">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h2 className="font-semibold text-charcoal-800 mb-2">¡Contraseña actualizada!</h2>
                <p className="text-sm text-charcoal-600">Redirigiendo al inicio de sesión…</p>
              </div>
            ) : !token ? (
              <div className="text-center py-6" data-testid="reset-no-token">
                <div className="w-14 h-14 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle size={28} />
                </div>
                <p className="text-sm text-charcoal-600 mb-4">El enlace no contiene un token válido.</p>
                <Link to="/forgot-password" className="text-sm font-medium text-cmic-600 hover:text-cmic-700">Solicitar uno nuevo</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5">Nueva contraseña</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                    <input
                      type={show ? 'text' : 'password'}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      required
                      minLength={8}
                      data-testid="reset-new-password"
                      autoFocus
                      placeholder="Mínimo 8 caracteres"
                      className="w-full rounded-xl border border-gray-200 pl-9 pr-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                    />
                    <button type="button" onClick={() => setShow((p) => !p)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-charcoal-400 hover:text-charcoal-600">
                      {show ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5">Confirmar contraseña</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                    <input
                      type={show ? 'text' : 'password'}
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      required
                      data-testid="reset-confirm-password"
                      placeholder="Repite la contraseña"
                      className="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                    />
                  </div>
                </div>
                {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="reset-error">{error}</div>}
                <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all duration-200 disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Cambiar contraseña'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
